import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MDBCol, MDBIcon, MDBRow, MDBSpinner } from "mdb-react-ui-kit";
import { saveAs } from "file-saver";
import styled from "styled-components";

import axiosGet from "../../helpers/axiosGet";
import axiosPost from "../../helpers/axiosPost";
import axiosDelete from "../../helpers/axiosDelete";
import getUploadErrorMessage from "../../helpers/getUploadErrorMessage";

import Button from "../Button/Button";
import CardWrapper from "../Card/CardWrapper";
import Modal from "../Modal/Modal";
import FloatingAlert from "../FloatingAlert";
import cargoDocs from "@provis/provis-common-be-module/dist/constants/claim/cargo";


// const fields = [
//   {
//     name: "DOC_CLAIM",
//     label: "Upload Formulir Klaim",
//   },
//   {
//     name: "DOC_STP3",
//     label: "Upload Surat Tuntutan Pihak Ke-3",
//   },
//   {
//     name: "DOC_AIRWAY",
//     label: "Bills of Lading / Airway",
//   },
//   {
//     name: "DOC_DELIVERY_RECEIPT",
//     label: "Surat Jalan / Delivery Receipt",
//   },
//   {
//     name: "DOC_PACKING_LIST",
//     label: "Packing List",
//   },
//   {
//     name: "DOC_INVOICE",
//     label: "Invoice Nilai Rincian Seluruh Muatan",
//   },
//   {
//     name: "DOC_CLAIM_REPORT",
//     label: "Berita Acara Kerugian",
//   },
//   {
//     name: "DOC_DOCUMENTATION",
//     label: "Foto - foto dokumentasi berwarna dan atau rekaman video (kontainer, seal, barang yang rusak)",
//   },
//   {
//     name: "DOC_INFORMATION_DESTROYED_GOODS",
//     label: "Berita Acara Pemusnahan Barang",
//   },
//   {
//     name: "DOC_DOCUMENTATION_DESTROYED_GOODS",
//     label: "Dokumentasi Pemusnahan Barang",
//   },
// ];

// const conditionalFields = [
//   {
//     name: "DOC_EIR",
//     label: "EIR Sebelum Stuffing",
//   },
//   {
//     name: "DOC_TALLY_SHEET",
//     label: "Tally Sheet",
//   },
//   {
//     name: "DOC_POLICE_REPORT",
//     label: "Surat Laporan Kepolisian",
//   },
// ];

function docKeyToField(key) {
  const desc =
    cargoDocs?.LIST_DESC?.[key]?.desc?.id ||
    cargoDocs?.LIST_DESC?.[key]?.desc?.en ||
    key;
  return { name: key, label: desc };
}

function uniquePreserveOrder(arr) {
  const seen = new Set();
  const out = [];
  for (const k of arr) {
    if (!seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

function buildRequiredDocKeys(data) {
  const base = [...(cargoDocs?.LIST_REQUIREMENT_WAITING_DOC || [])];

  const tol = (data?.typeOfLoss || "").toLowerCase();
  const isShortageLoss = tol === "shortage";
  const isTheftLoss = tol === "theft/burglary";
  if (!isShortageLoss && !isTheftLoss) {
    base.push(...(cargoDocs?.LIST_ADDITIONAL_TYPE_LOSS_NOT_SHORTAGE_AND_THEFT || []));
  }

  const acc = (data?.accidentType || "").toUpperCase();
  if (acc === "DAMAGE") {
    base.push(...(cargoDocs?.LIST_ADDITIONAL_DAMAGE_WAITING_DOC || []));
  } else if (acc === "SHORTAGE") {
    base.push(...(cargoDocs?.LIST_ADDITIONAL_SHORTAGE_WAITING_DOC || []));
  }

  return uniquePreserveOrder(base);
}

function buildFields(data) {
  const keys = buildRequiredDocKeys(data);
  return keys.map(docKeyToField);
}

const StyledInputColumn = styled.div`
  background: ${({ isExist }) => (isExist ? "#f5f5f5" : "#ffffff")};
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  span {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }

  div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const StyledInput = styled.div`
  /* background: #ffffff; */
  background: ${({ isExist }) => (isExist ? "#f5f5f5" : "#ffffff")};
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const StyledButton = styled.button`
  font-size: 14px;
  font-weight: 700;
  border-radius: 30px;
  color: white;
  min-height: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(props) =>
    props.isDelete ? "#F93154" : props.isDownload ? "#ADDFFF" : "#43BA94"};
  border: none;
  pointer-events: ${(props) => props.isUpload && "none"};
  min-width: ${(props) => (props.isDelete ? "40px" : "90px")};

  :disabled {
    background: #757575;
  }

  i {
    pointer-events: none;
  }
`;

const FileForm = ({
  formId,
  handleNext,
  data,
  docs,
  docsOther,
  disableNext,
  refreshData
}) => {
  const { accidentType, typeOfLoss } = data;

  const [isGlobalUploading, setIsGlobalUploading] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setError,
    clearErrors,
  } = useForm({ shouldUnregister: true });

  const computedFields = useMemo(() => buildFields({ typeOfLoss, accidentType }), [typeOfLoss, accidentType]);
  const onSubmit = (data) => {
    handleNext(data);
  };

  const filteredDocs = useMemo(
    () => docsOther.filter((x) => x.filepath),
    [docsOther]
  );

  return (
    <CardWrapper>
      <form onSubmit={handleSubmit(onSubmit)} id={formId}>
        <MDBRow className="g-4">
          {computedFields.map((field) => (
            <MDBCol size="12" key={field.name}>
              <Field
                field={field}
                rhf={{
                  register: register,
                  errors: errors,
                  watch: watch,
                  setError: setError,
                  clearErrors: clearErrors,
                }}
                data={data}
                docs={docs}
                disableNext={disableNext}
                setIsGlobalUploading={setIsGlobalUploading}
                isGlobalUploading={isGlobalUploading}
              />
            </MDBCol>
          ))}

          {/* {accidentType === "DAMAGE" && (
            <MDBCol size="12">
              <Field
                field={conditionalFields[0]}
                rhf={{
                  register: register,
                  errors: errors,
                  watch: watch,
                  setError: setError,
                  clearErrors: clearErrors,
                }}
                data={data}
                docs={docs}
                disableNext={disableNext}
              />
            </MDBCol>
          )}

          {accidentType === "SHORTAGE" &&
            conditionalFields.map((field) => (
              <MDBCol size="12" key={field.name}>
                <Field
                  field={field}
                  rhf={{
                    register: register,
                    errors: errors,
                    watch: watch,
                    setError: setError,
                    clearErrors: clearErrors,
                  }}
                  data={data}
                  docs={docs}
                  disableNext={disableNext}
                />
              </MDBCol>
            ))} */}

          {docsOther
            .filter((x) => !x.filepath)
            .map((field) => ({
              ...field,
              name: field.filename || field.documentName,
              label: field.documentName,
            }))
            .map((field, idx) => (
              <MDBCol size="12" key={`DOC_OTHER_${idx}`}>
                <Field
                  field={field}
                  rhf={{
                    register: register,
                    errors: errors,
                    watch: watch,
                    setError: setError,
                    clearErrors: clearErrors,
                  }}
                  data={data}
                  docs={docsOther.filter((x) => !x.filepath)}
                  disableNext={disableNext}
                />
              </MDBCol>
            ))}

          <FieldArray
            rhf={{
              register: register,
              errors: errors,
              setError: setError,
              clearErrors: clearErrors,
            }}
            data={data}
            disableNext={disableNext}
            docs={filteredDocs}
            refreshData={refreshData}
          />
        </MDBRow>
      </form>

      <MDBRow>
        <MDBCol size="12">
          <UploadOtherDocForm
            data={data}
            disableNext={disableNext}
            rhf={{
              register: register,
              errors: errors,
              setError: setError,
              clearErrors: clearErrors,
              handleSubmit: handleSubmit,
            }}
          />
        </MDBCol>
      </MDBRow>
    </CardWrapper>
  );
};

const Field = ({ field, rhf, data, docs, disableNext, setIsGlobalUploading, isGlobalUploading }) => {
  const { name, label, fileId } = field;
  const { register, errors, watch, setError, clearErrors } = rhf;
  const { claim } = data;

  const statusCode = claim?.statusCode;

  const value = watch(name);

  let doc = {};
  docs.forEach((element) => {
    if (element.type === name) {
      doc.isExist = true;
      doc.fileId = element.fileId;
      doc.claimCargoId = element.claimCargoId;
      doc.filename = element.filename;
      doc.description = element.description;
      doc.revisionNotes = element.revisionNotes;
    }
  });

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const handleLampirkan = (e) => {
    const input = e.target;
    const file = input.files[0];
    clearErrors(name);

    if (file) {
      //  max size DOC_DOCUMENTATION adalah 50MB
      if (name === "DOC_DOCUMENTATION" && file.size > 50_000_000) {
        setError(
          name,
          {
            type: "manual",
            message: "File too big, max 50MB",
          },
          {
            shouldFocus: true,
          }
        );
        // reset input supaya user bisa pilih file lagi
        input.value = "";
      }
      //  selain itu, max size adalah 15MB
      else if (file.size > 15_000_000) {
        setError(
          name,
          {
            type: "manual",
            message: "File too big, max 15MB",
          },
          {
            shouldFocus: true,
          }
        );
        // reset input supaya user bisa pilih file lagi
        input.value = "";
      } else {
        setUploadLoading(true);
        disableNext(true);
        setIsGlobalUploading(true);

        const fd = new FormData();

        fd.append("file", file);
        fd.append("noClaim", claim?.noClaim);
        if (fileId) {
          fd.append("fileId", fileId);
          fd.append("type", "DOC_OTHER");
        } else fd.append("type", name);

        axiosPost({
          url: fileId ? "/claim-cargo/upload/requested" : "/claim-cargo/upload",
          data: fd,
          callback: (res) => {
            if (fileId) {
              window.location.reload();
            }
            // cek tipe dokumen, apakah ada yang sama atau tidak
            const index = docs.findIndex((doc) => doc.type === name);

            // jika tidak, push dokumen baru
            if (index === -1) {
              docs.push(res.data);
            } else {
              // jika ada yang sama, replace dokumen yang lama
              docs[index] = res.data;
            }

            setUploadLoading(false);
            disableNext(false);
            setIsGlobalUploading(false);
          },
          errorCallback: (err) => {
            setUploadLoading(false);
            disableNext(false);
            setIsGlobalUploading(false);
            setError(name, { type: "manual", message: getUploadErrorMessage(err) });
            input.value = "";
            console.log(err);
          },
        });
      }
    }
  };

  const handleDownload = (fileId, filename) => {
    setDownloadLoading(true);

    axiosGet({
      url: `/claim-cargo/download/file-id/${fileId}`,
      responseType: "blob",
      callback: () => {
        setDownloadLoading(false);
      },
      errorCallback: (res) => {
        setDownloadLoading(false);

        let file = new File([res], filename);
        saveAs(file);
      },
    });
  };

  const canUpload =
    (["100", "110"].includes(statusCode) && !doc.isExist) ||
    (["110", "111", "201"].includes(statusCode) && doc.revisionNotes);

  return (
    <>
      <label htmlFor={name}>{label}</label>

      <input
        type="file"
        className="form-control form-control-lg"
        id={name}
        name={name}
        {...register(name, {
          onChange: handleLampirkan,
        })}
        style={{ display: "none" }}
        accept={
          name === "DOC_STP3"
            ? ".doc, .docx, .pdf"
            : ".jpg, .jpeg, .png, .pdf, .mp4, .3gp, .webm, .mkv, .avi, .mpeg, .m4v"
        }
        disabled={uploadLoading || isGlobalUploading}
      />

      <StyledInput isExist={doc.isExist}>
        <span>
          {value?.length
            ? value[0].name
            : doc.isExist
            ? doc.filename
            : "Pilih file"}
        </span>

        <div>
          {doc.isExist && (
            <StyledButton
              type="button"
              disabled={downloadLoading}
              onClick={() => handleDownload(doc.fileId, doc.filename)}
              isDownload
            >
              {downloadLoading ? (
                <MDBSpinner role="status" size="sm">
                  <span className="visually-hidden">Loading...</span>
                </MDBSpinner>
              ) : (
                "Download"
              )}
            </StyledButton>
          )}

          {canUpload && (
              <label htmlFor={name}>
                <div style={{ cursor: "pointer" }}>
                  <StyledButton
                    type="button"
                    disabled={uploadLoading || isGlobalUploading}
                    isUpload
                  >
                    {uploadLoading ? (
                      <MDBSpinner role="status" size="sm">
                        <span className="visually-hidden">Loading...</span>
                      </MDBSpinner>
                    ) : (
                      "Lampirkan"
                    )}
                  </StyledButton>
                </div>
              </label>
          )}
        </div>
      </StyledInput>

      {doc.revisionNotes && (
        <div
          style={{
            fontSize: "12px",
            marginTop: "10px",
            fontWeight: "bold",
            color: "red",
            fontStyle: "italic",
          }}
        >
          Notes: {doc.revisionNotes}
        </div>
      )}

      {errors[name] && (
        <p className="mb-0 mt-1 text-danger small">*{errors[name].message}</p>
      )}
    </>
  );
};

const FieldArray = ({ rhf, data, docs, disableNext, refreshData }) => {
  const [fields, setFields] = useState(docs);

  useEffect(() => {
    if (docs) {
      setFields(docs);
    }
  }, [docs]);

  const removeField = (id) => {
    const filteredDocs = fields.filter((field) => field.id !== id);
    setFields(filteredDocs);
  };

  const changeField = (data, id) => {
    const newFields = [...fields];

    const index = newFields.findIndex((field) => field.id === id);

    if (index === -1) {
      // do nothing
    } else {
      // change object value with new data
      newFields[index] = data;
      setFields(newFields);
    }
  };

  const addDescription = (desc, id) => {
    const newFields = [...fields];

    const newFieldsWithDesc = newFields.map((field) => {
      return field.id === id
        ? {
            ...field,
            description: desc,
          }
        : field;
    });

    setFields(newFieldsWithDesc);
  };

  return (
    <>
      <label>Dokumen Lainnya</label>
      <hr />
      {fields?.map((field, index) => (
        <MDBCol size="12" key={field.id}>
          <SingleFieldArray
            index={field.id}
            field={field}
            rhf={rhf}
            data={data}
            docs={docs}
            disableNext={disableNext}
            handleField={{
              changeField: changeField,
              removeField: removeField,
              addDescription: addDescription,
            }}
            refreshData={refreshData}
          />
        </MDBCol>
      ))}
    </>
  );
};

const SingleFieldArray = ({
  index,
  field,
  rhf,
  data,
  disableNext,
  handleField,
  refreshData
}) => {
  const name = `DOC_OTHER_${index}`;

  const desc = `${name}_DESC`;
  const descLabel = "Deskripsi Dokumen";
  const docNameLabel = "Nama Dokumen";

  const isExist = field.filename ? true : false;

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDownload = (fileId, filename) => {
    setDownloadLoading(true);

    axiosGet({
      url: `/claim-cargo/download/file-id/${fileId}`,
      responseType: "blob",
      callback: () => {
        setDownloadLoading(false);
      },
      errorCallback: (res) => {
        setDownloadLoading(false);

        let file = new File([res], filename);
        saveAs(file);
      },
    });
  };

  const handleDelete = (id, fileId) => {
    // jika file sudah diupload, hapus dari db
    if (isExist) {
      setDeleteLoading(true);
      axiosDelete({
        url: `/claim-cargo/delete/file-id/${fileId}`,
        callback: () => {
          setDeleteLoading(false);
          refreshData();
        },
        errorCallback: (err) => {
          setDeleteLoading(false);
          console.log(err);
        },
      });
    }
  };

  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = (fileId, field) => {
    setExpandedRows((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [field]: !prev[fileId]?.[field],
      },
    }));
  };

  const renderTextWithToggle = (text, fileId, field) => {
    const isExpanded = expandedRows[fileId]?.[field];
    const shouldTruncate = text?.length > 20;

    if (!shouldTruncate) return text || "-";

    return (
      <div>
        <div>
          <div>{isExpanded ? text : `${text.slice(0, 20)}...`}</div>
        </div>
        <div
          onClick={() => toggleExpand(fileId, field)}
          style={{
            color: "blue",
            cursor: "pointer",
            fontSize: "0.75rem",
            marginTop: "4px",
          }}
        >
          {isExpanded ? "Show less" : "Show more"}
        </div>
      </div>
    );
  };

  return (
    <>
      <MDBRow className="g-2">
        <MDBCol md="4">
          <label htmlFor={desc}>{docNameLabel}</label>
          {isExist && (
            <StyledInputColumn>
              {renderTextWithToggle(
                field?.documentName,
                field?.fileId,
                "documentName"
              )}
            </StyledInputColumn>
          )}
        </MDBCol>
        <MDBCol md="4">
          <label htmlFor={desc}>{descLabel}</label>
          {isExist && (
            <StyledInputColumn>
              {renderTextWithToggle(
                field?.description,
                field?.fileId,
                "description"
              )}
            </StyledInputColumn>
          )}
        </MDBCol>

        <MDBCol md="4">
          <label>File Name</label>
          {isExist && (
            <StyledInput>
              <span>{field.filename}</span>

              <div>
                <StyledButton
                  type="button"
                  disabled={downloadLoading}
                  onClick={() => handleDownload(field.fileId, field.filename)}
                  isDownload
                >
                  {downloadLoading ? (
                    <MDBSpinner role="status" size="sm">
                      <span className="visually-hidden">Loading...</span>
                    </MDBSpinner>
                  ) : (
                    "Download"
                  )}
                </StyledButton>

                <StyledButton
                  type="button"
                  onClick={() => handleDelete(field.id, field.fileId)}
                  disabled={deleteLoading}
                  isDelete
                >
                  {deleteLoading ? (
                    <MDBSpinner role="status" size="sm">
                      <span className="visually-hidden">Loading...</span>
                    </MDBSpinner>
                  ) : (
                    <MDBIcon fas icon="trash" />
                  )}
                </StyledButton>
              </div>
            </StyledInput>
          )}
        </MDBCol>
      </MDBRow>
    </>
  );
};

const UploadOtherDocForm = ({ data, disableNext }) => {
  const [modal, setModal] = useState(false);
  const toggleModal = () => setModal(!modal);

  const rhfModal = useForm();

  return (
    <>
      <MDBCol size="12" className="mt-4">
        <Button small onClick={toggleModal} startIcon="plus">
          DOKUMEN LAINNYA
        </Button>
      </MDBCol>

      <Modal
        show={modal}
        setShow={setModal}
        toggleModal={toggleModal}
        title="Upload Dokumen Lainnya"
      >
        <ModalForm
          toggleModal={toggleModal}
          rhf={rhfModal}
          data={data}
          disableNext={disableNext}
        />
      </Modal>
    </>
  );
};

const ModalForm = ({ toggleModal, rhf, data, disableNext }) => {
  const [selectedFileName, setSelectedFileName] = useState("Pilih file");

  const { register, errors, setError, handleSubmit } = rhf;
  const { noClaim } = data;
  const [uploadLoading, setUploadLoading] = useState(false);

  const [errorSubmit, setErrorSubmit] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  const handleSubmitOtherDoc = (data) => {
    if (data?.file.size > 50_000_000) {
      setError(
        "docOther",
        {
          type: "manual",
          message: "File too big, max 50MB",
        },
        {
          shouldFocus: true,
        }
      );
    } else {
      setUploadLoading(true);
      disableNext(true);

      const fd = new FormData();

      fd.append("noClaim", noClaim);
      fd.append("documentName", data.documentName);
      fd.append("file", data?.file[0]);
      fd.append("type", "DOC_OTHER");
      fd.append("description", data?.description);

      axiosPost({
        url: "/claim-cargo/upload",
        data: fd,
        callback: (res) => {
          setUploadLoading(false);
          setShowAlert(true);
          toggleModal();
          disableNext(false);
          window.location.reload();
        },
        errorCallback: (err) => {
          setShowAlert(true);
          setUploadLoading(false);
          disableNext(false);
          setErrorSubmit(err);
          setTimeout(() => {
            setShowAlert(false);
          }, 3000);
          console.log(err);
        },
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(handleSubmitOtherDoc)}>
      <MDBRow className="g-4">
        <MDBCol size="12">
          <label htmlFor="documentName">Nama Dokumen</label>
          <input
            type="text"
            className="form-control form-control-lg"
            id="documentName"
            name="documentName"
            {...register("documentName")}
          />
        </MDBCol>

        <MDBCol size="12">
          <label htmlFor="description">Deskripsi Dokumen</label>
          <input
            type="text"
            className="form-control form-control-lg"
            id="description"
            name="description"
            {...register("description")}
          />
        </MDBCol>

        <MDBCol size="12">
          <StyledInput>
            <span>{selectedFileName}</span>

            <div>
              <label htmlFor="file">
                <div style={{ cursor: "pointer" }}>
                  <StyledButton type="button" disabled={uploadLoading} isUpload>
                    {uploadLoading ? (
                      <MDBSpinner role="status" size="sm">
                        <span className="visually-hidden">Loading...</span>
                      </MDBSpinner>
                    ) : (
                      "Lampirkan"
                    )}
                  </StyledButton>
                </div>
              </label>
              <input
                type="file"
                style={{ display: "none" }}
                id="file"
                name="file"
                {...register("file", {
                  required: "Pilih file untuk diupload",
                  onChange: (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setSelectedFileName(file.name);
                    } else {
                      setSelectedFileName("Pilih file");
                    }
                  },
                })}
              />
            </div>
          </StyledInput>
          {errors?.file && (
            <p className="mb-0 mt-1 text-danger small">
              *{errors?.file.message}
            </p>
          )}
        </MDBCol>
      </MDBRow>

      <div className="d-flex justify-content-end mt-4">
        <Button small type={"submit"}>
          Upload
        </Button>
      </div>

      <FloatingAlert
        message={""}
        show={showAlert}
        error={errorSubmit}
        handleClose={() => setShowAlert(false)}
      />
    </form>
  );
};

export default FileForm;
